import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/*
 * REGRESO A LA PANTALLA DESDE LA QUE SE ENTRÓ · modo adicional del botón de
 * volver (RF-151, D-227, D-238, D-249, RN-414 a RN-421).
 *
 * POR QUÉ ES UN MODO Y NO UN CAMBIO DE `volverA`: el parámetro `volverA` del
 * armazón significa «la ruta padre fija» en sus 34 sitios de llamada repartidos
 * en 21 pantallas, y cambiarle el sentido obligaría a auditar 21 pantallas para
 * arreglar una (D-238, RN-418). El modo se construye aquí, en el núcleo, y se
 * aplica ÚNICAMENTE a la pantalla de Ajustes (D-249, RN-439). Queda disponible
 * para que el incremento 14 lo use en el Tablero sin volver a tocar el núcleo.
 *
 * CÓMO SE SABE DE DÓNDE SE ENTRÓ: el armazón anota cada paso propio de la
 * aplicación —y solo los propios— en un registro de tamaño acotado. El punto de
 * entrada de ESTA visita es el último paso anotado que NO pertenece a la pantalla
 * que pregunta: por eso entrar a Ajustes desde Cuentas, salir, entrar otra vez
 * desde Presupuesto y salir deja a la persona en Presupuesto y no en Cuentas
 * (RN-421). Y por eso bajar a una subpantalla de Ajustes y volver a Ajustes NO
 * cambia el punto de entrada (RN-420).
 *
 * LO QUE NO ES: no es el historial del navegador y no lo sustituye. No se
 * persiste —al recargar queda vacío, que es exactamente «no hay paso previo
 * propio» (RN-415)— y nunca contiene una dirección ajena a la aplicación, así que
 * la flecha no puede sacar a nadie de la aplicación (RN-393b).
 */

const MAXIMO_DE_PASOS = 12;

let pasos = [];

/** Anota un paso propio. Entrar dos veces a la misma dirección no la duplica. */
export function registrarPaso(direccion) {
  if (typeof direccion !== 'string' || !direccion.startsWith('/')) return;
  if (pasos[pasos.length - 1] === direccion) return;
  pasos = [...pasos, direccion].slice(-MAXIMO_DE_PASOS);
}

/** Solo para pruebas: deja el registro como recién arrancada la aplicación. */
export function limpiarPasos() {
  pasos = [];
}

export function obtenerPasos() {
  return [...pasos];
}

/**
 * Último paso anotado que no es de `rutaDeLaPantalla` ni de sus subpantallas. Si
 * no hay ninguno —entrada directa por dirección, icono instalado en el teléfono,
 * llegada tras una redirección que reemplazó el paso, primer arranque— devuelve
 * el respaldo, que para Ajustes es Movimientos (RN-415).
 */
export function obtenerPuntoDeEntrada(rutaDeLaPantalla, respaldo) {
  if (!rutaDeLaPantalla) return respaldo;

  for (let indice = pasos.length - 1; indice >= 0; indice -= 1) {
    const paso = pasos[indice];
    const soloRuta = paso.split('?')[0];
    const esDeEstaPantalla = soloRuta === rutaDeLaPantalla
      || soloRuta.startsWith(`${rutaDeLaPantalla}/`);
    if (!esDeEstaPantalla) return paso;
  }

  return respaldo;
}

/** Lo monta el armazón una sola vez: anota cada pantalla propia que se visita. */
export function useRastreoDePasos() {
  const ubicacion = useLocation();
  const direccion = `${ubicacion.pathname}${ubicacion.search}`;

  useEffect(() => { registrarPaso(direccion); }, [direccion]);

  return direccion;
}

/**
 * Punto de entrada de ESTA visita, calculado UNA VEZ al montar la pantalla: el
 * regreso no debe moverse mientras la persona está dentro (RN-421).
 */
export function usePuntoDeEntrada(rutaDeLaPantalla, respaldo) {
  const [puntoDeEntrada] = useState(() => obtenerPuntoDeEntrada(rutaDeLaPantalla, respaldo));
  return rutaDeLaPantalla ? puntoDeEntrada : respaldo;
}
